import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Hydration fix (26.07): "loading" (ekran ladowania w AppShell) startuje true TYLKO gdy jest
  // token do zweryfikowania. Bez tokenu (zawsze tak podczas crawla react-snap - pusty localStorage)
  // loading startuje false i AppShell od razu renderuje LoginScreen na pierwszym przebiegu -
  // dokladnie to co jest zamrozone w /app/index.html, wiec hydratacja sie zgadza. Bez tego kazdy
  // anonimowy wchodzacy na /app dostawal blysk "Ladowanie..." niezgodny z zamrozonym LoginScreen.
  const [loading, setLoading] = useState(() => !!localStorage.getItem('iso_token'));

  useEffect(() => {
    const token = localStorage.getItem('iso_token');
    if (!token) { setLoading(false); return; }
    fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(u => { if (u) setUser(u); else localStorage.removeItem('iso_token'); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await fetch('/api/auth/login', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error);
    localStorage.setItem('iso_token', data.token);
    setUser(data.user);
    await claimGapAuditTrialIfRequested(data.token);
  };

  // Rdzen: POSTuje do /claim-gap-audit-trial podanym tokenem, wtapia zwrocony gapAuditEntitlement do
  // stanu usera. Wspoldzielony przez dwa miejsca ktore doczepiaja trial do JUZ istniejacego konta:
  // (1) claimGapAuditTrialIfRequested ponizej - auto-fire po udanym login() gdy URL ma ?claimGapAudit=1
  // (submit formularza logowania = niejawne potwierdzenie), (2) AppShell.jsx - gdy JUZ zalogowany user
  // klika trial CTA na /gap-audit (?gapStarter=1) i JAWNIE potwierdza w oknie (23.07: nie wolno cicho
  // mutowac konta z samej nawigacji/URL). Eksportowana przez Context zeby AppShell mogl jej uzyc wprost.
  const claimGapAuditTrial = async (tok) => {
    const r = await fetch('/api/auth/claim-gap-audit-trial', {
      method: 'POST', headers: { Authorization: `Bearer ${tok}` },
    });
    if (!r.ok) throw new Error(`claim-gap-audit-trial: ${r.status} ${await r.text().catch(() => '')}`);
    const data = await r.json();
    setUser(u => u ? { ...u, gapAuditEntitlement: data.gapAuditEntitlement } : u);
    // 29.07: sygnal dla GTM/Google Ads (konwersja) - user JUZ majacy konto dopina sobie trial z /gap-audit,
    // osobna sciezka od swiezej rejestracji (patrz activateAccount ponizej). Push do dataLayer to
    // synchroniczna operacja na tablicy w pamieci, zero kosztu sieciowego/renderowania.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'gap_audit_trial_claimed', source: 'existing_account' });
    return data;
  };

  // Ktos rejestrujacy sie do Gap Audit na /gap-audit mailem ktory juz ma konto (np. Starter) jest
  // kierowany tutaj do logowania z ?claimGapAudit=1 w URL zamiast duplikatu konta - po udanym logowaniu
  // dopina sie gapAuditEntitlement do JEGO istniejacego konta. Usuwa parametr z URL zeby nie probowac
  // ponownie przy odswiezeniu strony.
  const claimGapAuditTrialIfRequested = async (token) => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('claimGapAudit') !== '1') return;
    try {
      await claimGapAuditTrial(token);
    } catch (e) { console.error('claim-gap-audit-trial nie powiodlo sie:', e.message); /* najwyzej user sprobuje ponownie z /gap-audit */ }
    params.delete('claimGapAudit');
    const newSearch = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
  };

  const register = async (email, password, name, lang) => {
    const r = await fetch('/api/auth/register', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, lang })
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error);
    if (data.pending) return { pending: true, email: data.email };
  };

  const registerGapAudit = async (email, password, name, lang) => {
    const r = await fetch('/api/auth/register-gap-audit', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, lang })
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error);
    if (data.accountExists) return { accountExists: true, email: data.email };
    if (data.pending) return { pending: true, email: data.email };
  };

  const activateAccount = async (token) => {
    const r = await fetch(`/api/auth/activate/${token}`);
    const data = await r.json();
    if (!r.ok) throw new Error(data.error);
    localStorage.setItem('iso_token', data.token);
    setUser(data.user);
    // 29.07: sygnal dla GTM/Google Ads (konwersja) - to jest prawdziwy moment "konto istnieje i dziala",
    // nie sam submit formularza w register() ponizej (ten tylko wysyla mail aktywacyjny, {pending:true}).
    // gapAuditEntitlement obecny na koncie w tym momencie = user zarejestrowal sie przez /gap-audit
    // (registerGapAudit), wiec to rowniez liczy sie jako start triala Gap Audit - osobne zdarzenie.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'signup_complete' });
    if (data.user.gapAuditEntitlement) window.dataLayer.push({ event: 'gap_audit_trial_claimed', source: 'signup' });
  };

  const logout = () => {
    localStorage.removeItem('iso_token');
    setUser(null);
  };

  const token = () => localStorage.getItem('iso_token');

  const refreshUser = async () => {
    const t = localStorage.getItem('iso_token');
    if (!t) return;
    const r = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${t}` } });
    if (r.ok) { const u = await r.json(); setUser(u); }
  };

  const acceptDisclaimer = async () => {
    const t = localStorage.getItem('iso_token');
    if (!t) throw new Error('Brak tokena');
    const r = await fetch('/api/auth/accept-disclaimer', {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!r.ok) throw new Error('Blad serwera');
    const data = await r.json();
    setUser(u => u ? { ...u, disclaimerAcceptedAt: data.disclaimerAcceptedAt } : u);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, registerGapAudit, activateAccount, logout, token, refreshUser, acceptDisclaimer, claimGapAuditTrial }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
